import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Mail, X as XIcon } from 'lucide-react';
import { fetchInviteSummary, InviteSummary } from '../../services/api/inviteService';
import {
  INSTAGRAM_URL,
  buildFacebookShareUrl,
  buildLineShareUrl,
  buildMailtoUrl,
  buildShareText,
  buildXShareUrl,
} from './inviteShare';

const OVERLAY =
  'fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4';
const PANEL =
  'w-full max-w-[520px] max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-[0_20px_50px_rgba(0,0,0,0.2)]';
const HEADER = 'border-b border-slate-200 px-6 py-5';
const TITLE = 'm-0 text-lg font-bold text-slate-900';
const SUBTITLE = 'mt-1.5 mb-0 text-sm leading-6 text-slate-600';
const BODY = 'px-6 py-5 flex flex-col gap-5';
const LABEL = 'mb-1.5 block text-xs font-medium text-slate-500';
const URL_INPUT =
  'flex-1 min-w-0 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700';
const BTN_PRIMARY =
  'inline-flex items-center gap-1.5 shrink-0 cursor-pointer rounded border-none bg-primary px-4 py-2 ' +
  'text-sm text-white transition-colors hover:bg-primary-hover';
const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-1.5 cursor-pointer rounded border border-slate-300 ' +
  'bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100';
const SHARE_GRID = 'grid grid-cols-4 gap-2 max-[480px]:grid-cols-2';
const SHARE_BTN =
  'flex cursor-pointer flex-col items-center gap-1 rounded border border-slate-200 bg-white ' +
  'px-2 py-3 text-xs text-slate-700 transition-colors hover:bg-slate-50';
const STATS = 'grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-4 text-center max-[480px]:grid-cols-1';
const STAT_LABEL = 'm-0 text-xs text-slate-500';
const STAT_VALUE = 'm-0 mt-1 text-base font-bold text-slate-900';
const FOOTER = 'flex justify-end border-t border-slate-200 px-6 py-4';
const ERROR_BOX = 'rounded bg-red-50 px-3 py-2 text-sm text-red-700';

interface FriendInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 友だち招待（組織外招待）モーダル。
 *
 * プラン・課金状態では一切分岐しない（仕様 2: PERSONAL の課金中でもトライアル中でも
 * 同じように招待できる）。分岐を足さないこと。
 */
const FriendInviteModal: React.FC<FriendInviteModalProps> = ({ isOpen, onClose }) => {
  const [summary, setSummary] = useState<InviteSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [instagramCopied, setInstagramCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setError(null);

    fetchInviteSummary()
      .then((data) => {
        if (!cancelled) {
          setSummary(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('招待情報の取得に失敗しました。時間をおいて再度お試しください。');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard?.writeText(text);
      return true;
    } catch {
      return false;
    }
  }, []);

  if (!isOpen) {
    return null;
  }

  const inviteUrl = summary?.invite_url ?? '';
  // 組織未所属（オンボ未完了）だとサーバー側で招待リンクを発行しない。
  // 空URLのまま共有ボタンを出すと壊れたリンクを配ってしまうので出さない。
  const hasInviteLink = Boolean(summary?.invite_url);

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    if (await copyToClipboard(inviteUrl)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleInstagram = async () => {
    // Instagram には Web の共有インテントが無いため、文面をコピーしてから開く。
    if (await copyToClipboard(buildShareText(inviteUrl))) {
      setInstagramCopied(true);
      window.setTimeout(() => setInstagramCopied(false), 4000);
    }
    openShareWindow(INSTAGRAM_URL);
  };

  return (
    <div className={OVERLAY} role="dialog" aria-modal="true" aria-label="友だちを招待">
      <div className={PANEL}>
        <div className={HEADER}>
          <h2 className={TITLE}>友だちを招待すると、あなたに 1,000cr</h2>
          <p className={SUBTITLE}>
            登録された時に 500cr、その方が最初のバナーを作った時に 500cr が入ります。
          </p>
        </div>

        <div className={BODY}>
          {error && <div className={ERROR_BOX}>{error}</div>}

          {summary && !hasInviteLink && (
            <div className={ERROR_BOX}>
              招待リンクを発行できませんでした。会社情報の登録を済ませてからお試しください。
            </div>
          )}

          {summary && hasInviteLink && (
            <>
              <div>
                <label className={LABEL} htmlFor="invite-url">
                  あなた専用の招待リンク
                </label>
                <div className="flex gap-2">
                  <input id="invite-url" className={URL_INPUT} value={inviteUrl} readOnly />
                  <button type="button" className={BTN_PRIMARY} onClick={handleCopy}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'コピー済み' : 'コピー'}
                  </button>
                </div>
              </div>

              <div>
                <span className={LABEL}>共有する</span>
                <div className={SHARE_GRID}>
                  <button
                    type="button"
                    className={SHARE_BTN}
                    aria-label="Xでシェア"
                    onClick={() => openShareWindow(buildXShareUrl(inviteUrl))}
                  >
                    <XIcon size={18} />
                    <span>X</span>
                  </button>
                  <button
                    type="button"
                    className={SHARE_BTN}
                    aria-label="Instagramでシェア"
                    onClick={handleInstagram}
                  >
                    <span aria-hidden>📷</span>
                    <span>Instagram</span>
                  </button>
                  <button
                    type="button"
                    className={SHARE_BTN}
                    aria-label="Facebookでシェア"
                    onClick={() => openShareWindow(buildFacebookShareUrl(inviteUrl))}
                  >
                    <span aria-hidden>f</span>
                    <span>Facebook</span>
                  </button>
                  <button
                    type="button"
                    className={SHARE_BTN}
                    aria-label="LINEで送る"
                    onClick={() => openShareWindow(buildLineShareUrl(inviteUrl))}
                  >
                    <span aria-hidden>💬</span>
                    <span>LINE</span>
                  </button>
                </div>
                {instagramCopied && (
                  <p className="mt-2 mb-0 text-xs text-slate-600">
                    文面をコピーしました。Instagram に貼り付けてください。
                  </p>
                )}
              </div>

              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() => openShareWindow(buildMailtoUrl(inviteUrl))}
              >
                <Mail size={16} />
                メールで送る
              </button>

              <div className={STATS}>
                <div>
                  <p className={STAT_LABEL}>登録された人</p>
                  <p className={STAT_VALUE}>{summary.registered_count}人</p>
                </div>
                <div>
                  <p className={STAT_LABEL}>獲得クレジット</p>
                  <p className={STAT_VALUE}>{summary.earned_credits.toLocaleString()}cr</p>
                </div>
                <div>
                  <p className={STAT_LABEL}>今月の残り</p>
                  <p className={STAT_VALUE}>あと{summary.monthly_remaining}人</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={FOOTER}>
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default FriendInviteModal;
